import {
  type DefaultValue as ConformDefaultValue,
  type SubmissionResult,
  useForm,
} from '@conform-to/react';
import { useActionData } from 'react-router';

import { type UseCustomFetcherReturn } from '@veraclins-dev/react-utils';
import {
  Empty,
  type SchemaType,
  type Values,
  type z,
} from '@veraclins-dev/utils';

import { getZodConstraint } from '../../zod/constraint';
import { parseWithZod } from '../../zod/parse';

/**
 * Re-exported for convenience. Use `DefaultValue` to type the `defaultValue`
 * option of {@link useConform}.
 */
export type DefaultValue<T> = ConformDefaultValue<T>;

type Options<S extends SchemaType> = Parameters<typeof useForm<Values<S>>>[0];

/**
 * Options for {@link useConform}. Extends Conform's `useForm` options
 * with a Zod schema, a required `id`, an optional `defaultValue`, and
 * an optional {@link useCustomFetcher} when submitting via a fetcher.
 *
 * Every other `useForm` option is forwarded as-is, so `shouldValidate`,
 * `shouldDirtyConsider`, `defaultNoValidate` and the rest work here.
 * `lastResult` is omitted because the hook reads it from `useActionData()`
 * or the fetcher itself.
 */
export interface ConformOptions<
  S extends SchemaType,
  L extends { submission?: SubmissionResult } = {
    submission?: SubmissionResult;
  },
> extends Omit<Options<S>, 'lastResult'> {
  /**
   * Zod schema, built with `zod/mini` or classic `zod`. Drives client
   * constraints and validation. Conform reads constraints once, when the
   * form is created.
   */
  schema?: S;
  /** Stable form id. Required by Conform for field id derivation. */
  id: string;
  defaultValue?: DefaultValue<Values<S>>;
  /** Pass when submitting through a fetcher so the hook reads `fetcher.data.submission`. */
  fetcher?: UseCustomFetcherReturn<L>;
}

/**
 * Conform's `useForm` wired up for the workspace's conventions:
 *
 * - Reads the previous submission result from `useActionData()` (or
 *   `fetcher.data` when a fetcher is supplied).
 * - Derives client-side constraints from the Zod schema via
 *   `getZodConstraint`.
 * - Validates with the package's `parseWithZod`, which loads with the hook,
 *   so a submit, `form.validate()` or blur validates at once.
 * - Revalidates on blur (Conform's `shouldRevalidate: 'onBlur'`).
 *
 * The schema-derived `constraint`, `onValidate` and `shouldRevalidate` are
 * defaults. Pass your own to override them. `id`, `defaultValue` and
 * `lastResult` stay owned by the hook. Any other `useForm` option is
 * forwarded untouched, including `shouldDirtyConsider`, which you need to
 * keep `form.dirty` honest when the form renders inputs Conform does not
 * manage:
 *
 * ```ts
 * const HONEYPOT_FIELDS = ['name__confirm', 'from__confirm'];
 * const { form } = useConform({
 *   id: 'contact-form',
 *   schema,
 *   shouldDirtyConsider: (name) => !HONEYPOT_FIELDS.includes(name),
 * });
 * ```
 *
 * Without it, the honeypot's pre-filled `from__confirm` input latches
 * `form.dirty` to `true` on the first edit and it never clears.
 *
 * Returns `{ form, fields, success }` where `success` is `true` only
 * when validation passed and a submission has been processed.
 */
export const useConform = <
  S extends SchemaType,
  L extends { submission?: SubmissionResult } = {
    submission: SubmissionResult;
  },
>({
  schema: sch,
  id,
  defaultValue,
  fetcher,
  ...options
}: ConformOptions<S>): {
  form: ReturnType<typeof useForm<z.infer<S>>>[0];
  fields: ReturnType<typeof useForm<z.infer<S>>>[1];
  success: boolean;
} => {
  const actionData = useActionData<L>();
  const schema = (sch ?? Empty) as S;

  const submission = actionData?.submission ?? fetcher?.data?.['submission'];
  const [form, fields] = useForm<z.infer<S>>({
    ...options,
    id,
    lastResult: submission,
    defaultValue: defaultValue ?? undefined,
    constraint: options.constraint ?? getZodConstraint(schema),
    shouldRevalidate: options.shouldRevalidate ?? 'onBlur',
    onValidate:
      options.onValidate ??
      (({ formData }) => parseWithZod(formData, { schema })),
  });

  return {
    form,
    fields,
    success: Boolean(form.status !== 'error' && form.valid && submission),
  };
};

/**
 * Conform `fields` object for a given Zod schema. Convenience alias so
 * field-rendering components can be typed without re-deriving the
 * `useConform` return shape.
 */
export type FormFields<T extends SchemaType> = ReturnType<
  typeof useConform<T>
>['fields'];
